// src/pages/Accounting/Reconcile/components/AccountPicker.tsx

import type React from "react";
import { useMemo, useRef, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { ACCOUNT_KIND_GROUP_LABELS, ACCOUNT_KIND_GROUP_ORDER, ACCOUNT_KIND_LABELS, PALETTE } from "../constants";
import type { Account } from "../types";
import { formatAccountLabel } from "../utils";

interface AccountPickerProps {
  accounts: Account[];
  valueId: string;
  onChange: (accountId: string) => void;
}

export function AccountPicker({ accounts, valueId, onChange }: AccountPickerProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const current = accounts.find((a) => a.id === valueId);

  const groups = useMemo(() => {
    const seenGroups: string[] = [];
    const byGroup: Record<string, Account[]> = {};
    for (const kind of ACCOUNT_KIND_GROUP_ORDER) {
      const groupLabel = ACCOUNT_KIND_GROUP_LABELS[kind];
      const forKind = accounts.filter((a) => a.kind === kind);
      if (forKind.length === 0) continue;
      if (!byGroup[groupLabel]) {
        byGroup[groupLabel] = [];
        seenGroups.push(groupLabel);
      }
      byGroup[groupLabel].push(...forKind);
    }
    return seenGroups.map((label) => ({ label, accounts: byGroup[label] }));
  }, [accounts]);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={triggerStyle}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          {current?.vela && <span style={vMarkStyle}>V</span>}
          <span>{current ? formatAccountLabel(current) : "Choose an account"}</span>
          {current && (
            <span style={{ color: PALETTE.ink3, fontWeight: 500, fontSize: 12.5 }}>
              &middot; {ACCOUNT_KIND_LABELS[current.kind]}
            </span>
          )}
        </span>
        <ChevronDown size={14} color={PALETTE.ink4} />
      </button>

      {open && (
        <div style={popStyle}>
          {groups.map((g) => (
            <div key={g.label}>
              <div style={groupLabelStyle}>{g.label}</div>
              {g.accounts.map((a) => {
                const isSelected = a.id === valueId;
                return (
                  <div
                    key={a.id}
                    style={{ ...rowStyle, background: isSelected ? PALETTE.bgFade : "transparent" }}
                    onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = PALETTE.bgFade; }}
                    onMouseLeave={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                    onClick={() => { onChange(a.id); setOpen(false); }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      {a.vela && <span style={vMarkStyle}>V</span>}
                      <span style={nameStyle}>{formatAccountLabel(a)}</span>
                    </span>
                    <span style={typeStyle}>{ACCOUNT_KIND_LABELS[a.kind]}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const triggerStyle: React.CSSProperties = { width: "100%", padding: "11px 14px", border: `1px solid ${PALETTE.line}`, borderRadius: 8, background: "#fff", fontSize: 14.5, color: PALETTE.ink, fontWeight: 500, fontFamily: "inherit", outline: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, cursor: "pointer", textAlign: "left" };
const popStyle: React.CSSProperties = { position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "#fff", border: `1px solid ${PALETTE.line}`, borderRadius: 12, boxShadow: "0 10px 32px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.05)", padding: "6px 0", zIndex: 20, maxHeight: 320, overflow: "auto" };
const groupLabelStyle: React.CSSProperties = { padding: "10px 14px 4px", fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: PALETTE.ink4, fontWeight: 700 };
const rowStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 14px", cursor: "pointer", gap: 10 };
const nameStyle: React.CSSProperties = { fontSize: 13.5, color: PALETTE.ink, fontWeight: 600 };
const typeStyle: React.CSSProperties = { fontSize: 12, color: PALETTE.ink3, fontWeight: 500 };
const vMarkStyle: React.CSSProperties = { background: PALETTE.vela, color: "#fff", width: 15, height: 15, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 8.5, fontWeight: 700, flexShrink: 0 };